import React from "react";
import {
  Braces,
  FileCode2,
  FileText,
  Gauge,
  Menu,
  RefreshCw,
  Route,
  ShieldCheck,
  UploadCloud,
  X,
} from "lucide-react";
import FileView from "./components/FileView";
import InstructionView from "./components/InstructionView";
import Overview from "./components/Overview";
import RequirementsView from "./components/RequirementsView";
import RubricView from "./components/RubricView";
import Sidebar from "./components/Sidebar";
import TrajectoryView from "./components/TrajectoryView";
import { Empty } from "./components/Primitives";
import { readDropItems, readLocalFiles } from "./lib/localFiles";
import { parseWorkspace } from "./lib/parser";
import { loadPublishedArchive, loadPublishedArchives } from "./lib/publishedArchives";
import { LocaleProvider, useLocale } from "./lib/i18n";

const tabs = [
  ["overview", "tab.overview", Gauge],
  ["instruction", "tab.instruction", FileText],
  ["rubrics", "tab.rubrics", Braces],
  ["trajectory", "tab.trajectory", Route],
  ["requirements", "tab.requirements", ShieldCheck],
  ["files", "tab.files", FileCode2],
];

const LanguageSwitch = () => {
  const { locale, setLocale, t } = useLocale();
  return <div className="language-switch" aria-label={t("language.label")}>
    {[["zh", "language.zh"], ["en", "language.en"]].map(([value, key]) => (
      <button type="button" key={value} className={locale === value ? "active" : ""} onClick={() => setLocale(value)}>{t(key)}</button>
    ))}
  </div>;
};

const Intro = ({ onOpen, onOpenArchive, onLoadLocal, onLoadPublished, loading, publishedLoading }) => {
  const { t } = useLocale();
  return (
  <main className="intro-shell">
    <div className="intro-grid" />
    <div className="intro-language"><LanguageSwitch /></div>
    <div className="intro-content">
      <div className="intro-kicker"><span />{t("intro.kicker")}</div>
      <h1>{t("intro.titleA")}<br /><em>{t("intro.titleB")}</em></h1>
      <p>{t("intro.body")}</p>
      <div className="intro-actions">
        <button className="primary-action large-action" onClick={onOpen}><UploadCloud size={17} />{t("intro.openDirectory")}</button>
        <button className="secondary-action large-action" onClick={onOpenArchive}><FileCode2 size={17} />{t("intro.openBundle")}</button>
        <button className="secondary-action large-action" onClick={onLoadLocal} disabled={loading}><RefreshCw size={17} className={loading ? "spin" : ""} />{loading ? t("intro.loading") : t("intro.loadProject")}</button>
        <button className="secondary-action large-action" onClick={onLoadPublished} disabled={publishedLoading}><FileCode2 size={17} className={publishedLoading ? "spin" : ""} />{publishedLoading ? t("intro.loadingPublished") : t("intro.loadPublished")}</button>
      </div>
      <div className="intro-footnotes"><span>01 · {t("intro.foot.browser")}</span><span>02 · {t("intro.foot.score")}</span><span>03 · {t("intro.foot.pages")}</span></div>
    </div>
    <div className="intro-orbit"><span className="orbit-a" /><span className="orbit-b" /><span className="orbit-c" /><i>HL</i></div>
  </main>
  );
};

function AppContent() {
  const { t } = useLocale();
  const [workspace, setWorkspace] = React.useState(null);
  const [selectedId, setSelectedId] = React.useState("");
  const [tab, setTab] = React.useState("overview");
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [publishedLoading, setPublishedLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [dragging, setDragging] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const directoryInputRef = React.useRef(null);
  const archiveInputRef = React.useRef(null);

  const loadEntries = React.useCallback(async (entries) => {
    const parsed = await parseWorkspace(entries);
    setWorkspace(parsed);
    setSelectedId((current) => parsed.samples.some((sample) => sample.id === current) ? current : parsed.samples[0]?.id || "");
    setError(parsed.samples.length ? "" : t("error.noSamples"));
  }, [t]);

  const loadFiles = async (files) => {
    setLoading(true);
    setError("");
    try { await loadEntries(await readLocalFiles(files)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setLoading(false); }
  };

  const loadLocal = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/__harbor_lens_local__");
      if (!response.ok) throw new Error(t("error.staticDisk"));
      if (!response.headers.get("content-type")?.includes("application/json")) {
        throw new Error(t("error.staticDisk"));
      }
      const payload = await response.json();
      if (!Array.isArray(payload.entries)) throw new Error(payload.error || t("error.bundleShape"));
      await loadEntries(payload.entries);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally { setLoading(false); }
  };

  const loadPublished = async () => {
    setPublishedLoading(true);
    setError("");
    try {
      const archives = await loadPublishedArchives();
      if (!archives.length) throw new Error(t("error.publishedEmpty"));
      const entries = await loadPublishedArchive(archives[0]);
      await loadEntries(entries);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setPublishedLoading(false);
    }
  };

  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).get("demo") === "1" && !workspace) {
      loadLocal();
    }
  }, [workspace]);

  const onDrop = async (event) => {
    event.preventDefault();
    setDragging(false);
    setLoading(true);
    try { await loadEntries(await readDropItems(event.dataTransfer)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setLoading(false); }
  };

  const sample = workspace?.samples.find((item) => item.id === selectedId) || workspace?.samples[0];
  const renderTab = () => {
    if (!sample) return <Empty title={t("empty.noSample")} body={t("empty.noSampleBody")} />;
    if (tab === "instruction") return <InstructionView sample={sample} />;
    if (tab === "rubrics") return <RubricView sample={sample} />;
    if (tab === "trajectory") return <TrajectoryView sample={sample} />;
    if (tab === "requirements") return <RequirementsView sample={sample} />;
    if (tab === "files") return <FileView sample={sample} />;
    return <Overview sample={sample} onJump={setTab} />;
  };

  return (
    <div
      className={`app ${dragging ? "is-dragging" : ""}`}
      onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
      onDrop={onDrop}
    >
      <input
        ref={directoryInputRef}
        className="hidden-input"
        type="file"
        multiple
        webkitdirectory=""
        onChange={(event) => event.target.files?.length && loadFiles(event.target.files)}
      />
      <input
        ref={archiveInputRef}
        className="hidden-input"
        type="file"
        accept=".zip,.json,.harborlens.json,application/zip,application/json"
        onChange={(event) => event.target.files?.length && loadFiles(event.target.files)}
      />
      {dragging ? <div className="drop-overlay"><UploadCloud size={32} /><strong>{t("drop.title")}</strong><span>{t("drop.body")}</span></div> : null}

      {!workspace ? (
        <>
          <Intro onOpen={() => directoryInputRef.current?.click()} onOpenArchive={() => archiveInputRef.current?.click()} onLoadLocal={loadLocal} onLoadPublished={loadPublished} loading={loading} publishedLoading={publishedLoading} />
          {error ? <div className="global-error">{error}</div> : null}
        </>
      ) : (
        <div className="workspace-shell">
          <div className={`sidebar-wrap ${mobileOpen ? "mobile-open" : ""}`}>
            <Sidebar
              workspace={workspace}
              selectedId={sample?.id}
              onSelect={(id) => { setSelectedId(id); setMobileOpen(false); }}
              query={query}
              onQuery={setQuery}
              onOpenDirectory={() => directoryInputRef.current?.click()}
              onOpenArchive={() => archiveInputRef.current?.click()}
              onLoadLocal={loadLocal}
              onLoadPublished={loadPublished}
              loading={loading}
              publishedLoading={publishedLoading}
            />
          </div>
          <div className="main-column">
            <header className="topbar">
              <button className="mobile-menu" onClick={() => setMobileOpen((value) => !value)}>{mobileOpen ? <X /> : <Menu />}</button>
              <div className="sample-heading">
                <div className="eyebrow">{sample?.domain} / {sample?.difficulty} / v{sample?.version}</div>
                <h1>{sample?.id}</h1>
              </div>
              <div className="topbar-status">
                <span className={sample?.analysis.passed ? "live-dot pass" : "live-dot fail"} />
                {sample?.analysis.passed ? t("status.requirementsClear") : t("status.blockers", { count: sample?.analysis.errors })}
              </div>
              <LanguageSwitch />
            </header>
            <nav className="tabbar">
              {tabs.map(([value, labelKey, Icon]) => (
                <button key={value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}><Icon size={14} />{t(labelKey)}</button>
              ))}
            </nav>
            {error ? <div className="workspace-error">{error}</div> : null}
            <main className="content">{renderTab()}</main>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return <LocaleProvider><AppContent /></LocaleProvider>;
}
